import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/tailwind-merge.ts'

const buttonVariants = cva(
	`inline-flex items-center justify-center whitespace-nowrap rounded-full transform-gpu transition-all duration-150 ease-in-out disabled:pointer-events-none disabled:bg-muted-500 disabled:text-brand-white disabled:opacity-60`,
	{
		variants: {
			variant: {
				default: `bg-brand-deep-pink text-brand-grey-light-1 hover:bg-brand-dark-pink hover:scale-[1.02] hover:shadow-lg hover:shadow-brand-deep-pink/20 active:scale-[0.98] active:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-deep-pink focus-visible:ring-offset-2 focus-visible:ring-offset-background`,
				destructive: `bg-danger-bg text-danger-text hover:bg-danger-bg-hover hover:scale-[1.02] hover:shadow-lg hover:shadow-danger-bg/20 active:scale-[0.98] active:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-bg focus-visible:ring-offset-2 focus-visible:ring-offset-background`,
				outline: `border border-brand-grey-light-1 text-brand-grey-light-1 hover:bg-brand-grey-light-1/10 hover:border-brand-grey-light-1/80 hover:scale-[1.02] active:scale-[0.98] active:bg-brand-grey-light-1/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-grey-light-1 focus-visible:ring-offset-2 focus-visible:ring-offset-background`,
				secondary: `border-transparent bg-muted-300 text-muted-900 hover:bg-muted-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] active:bg-muted-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-muted-300 focus-visible:ring-offset-2 focus-visible:ring-offset-background`,
				ghost: `text-brand-white hover:bg-brand-white/10 hover:scale-[1.02] active:scale-[0.98] active:bg-brand-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background`,
				link: `text-brand-white underline-offset-4 hover:underline hover:brightness-110 active:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-background`,
			},
			size: {
				default: `h-10 px-4 py-2`,
				wide: `px-24 py-5`,
				sm: `h-9 px-3`,
				lg: `h-11 px-8`,
				pill: `px-12 py-3 leading-3`,
				icon: `h-10 w-10`,
				nested: `h-7 px-7`,
			},
		},
		defaultVariants: {
			variant: `default`,
			size: `default`,
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : `button`
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				{...props}
			/>
		)
	},
)
Button.displayName = `Button`

export { Button, buttonVariants }
